import {
  Flex,
  Text,
  Box,
  NumberInput,
  InputGroup,
  NumberInputField,
  InputRightElement,
} from '@chakra-ui/react';
import React, { useEffect, useMemo, useState } from 'react';

import {
  finleyAssert,
  finleyAssertUnreachable,
} from '@finley-technologies/common';
import { Assignment } from '@finley-technologies/finley-types';

import { colors } from 'packages/common-frontend/colors';
import {
  AddIconButton,
  DeleteIconButton,
} from 'packages/common-frontend/components/IconButton';
import { Icon } from 'packages/common-frontend/components/icons';
import { Select } from 'packages/common-frontend/components/Select';
import { GetCreditFacilitiesAndLoanProductsWithBorrowingBaseMechanicsForAssignmentGroupingQueryGQLType } from 'packages/credit-facility/requests/requests.gql.generated';

type Target =
  | {
      name: string;
      type: 'creditFacility';
      creditFacilityId: string;
    }
  | {
      name: string;
      type: 'balanceSheet';
    };

const WaterfallConfiguration = ({
  config,
  idx,
  readOnly,
  deleteTarget,
  handleLimitChange,
  handlePercentageChange,
  allTargets,
  handleValueChange,
}: {
  config: Assignment.WaterfallByExcessMarginConfig;
  idx: number;
  readOnly: boolean;
  deleteTarget: (idx: number) => void;
  handleLimitChange: (limitType: 'min' | 'max', idx: number) => void;
  handlePercentageChange: (percentage: number | undefined, idx: number) => void;
  handleValueChange: (value: number | undefined, idx: number) => void;

  allTargets: Record<string, Target>;
}) => {
  const [localPercentage, setLocalPercentage] = useState<string | undefined>();
  const [localValue, setLocalValue] = useState<string | undefined>();

  useEffect(() => {
    setLocalPercentage(
      typeof config.percentageRate === 'number'
        ? (config.percentageRate * 100).toFixed(1)
        : undefined,
    );
  }, [config.percentageRate]);

  useEffect(() => {
    setLocalValue(
      typeof config.value === 'number' ? config.value.toFixed(0) : undefined,
    );
  }, [config.value]);

  return (
    <Box
      key={
        config.type === 'creditFacility'
          ? config.creditFacilityId
          : 'balanceSheet'
      }
      mb="20px"
    >
      <Flex alignItems="center">
        <Text>
          {allTargets[
            config.type === 'creditFacility'
              ? config.creditFacilityId
              : 'balanceSheet'
          ]?.name ?? ''}
        </Text>
        {!readOnly && <DeleteIconButton onClick={() => deleteTarget(idx)} />}
      </Flex>

      <Flex alignItems="center">
        <Text width="xs">Limit Type:</Text>
        <Select
          value={config.limitType}
          options={[
            { label: 'Min', value: 'min' as const },
            { label: 'Max', value: 'max' as const },
          ]}
          buttonProps={{ width: 'xs' }}
          onChange={(limitType) => {
            if (limitType) {
              handleLimitChange(limitType, idx);
            }
          }}
          isDisabled={readOnly}
        />
      </Flex>
      <Flex alignItems="center">
        <Text width="xs">Percentage:</Text>
        <InputGroup width="xs">
          <NumberInput
            value={localPercentage}
            precision={1}
            onChange={setLocalPercentage}
            onBlur={() => {
              if (localPercentage === '') {
                handlePercentageChange(undefined, idx);
              } else {
                const number = Number(localPercentage);

                if (!Number.isNaN(number)) {
                  handlePercentageChange(number / 100, idx);
                } else {
                  handlePercentageChange(undefined, idx);
                }
              }
            }}
            textAlign="right"
            isDisabled={readOnly}
            width="xs"
          >
            <NumberInputField background="#f9f6f1b3" />
            <InputRightElement>
              <Icon icon="percent" color="grey" />
            </InputRightElement>
          </NumberInput>
        </InputGroup>
      </Flex>
      <Flex alignItems="center">
        <Text width="xs">Value:</Text>
        <InputGroup width="xs">
          <NumberInput
            value={localValue}
            precision={1}
            onChange={setLocalValue}
            onBlur={() => {
              if (localValue === '') {
                handleValueChange(undefined, idx);
              } else {
                const number = Number(localValue);

                if (!Number.isNaN(number)) {
                  handleValueChange(number, idx);
                } else {
                  handleValueChange(undefined, idx);
                }
              }
            }}
            textAlign="right"
            isDisabled={readOnly}
            width="xs"
          >
            <NumberInputField background="#f9f6f1b3" />
          </NumberInput>
        </InputGroup>
      </Flex>
    </Box>
  );
};

export const WaterfallByExcessMargin = ({
  creditFacilitiesForAssignmentGroupingData,
  heuristicConfig,
  setHeuristicConfig,
  readOnly,
}: {
  creditFacilitiesForAssignmentGroupingData: GetCreditFacilitiesAndLoanProductsWithBorrowingBaseMechanicsForAssignmentGroupingQueryGQLType;
  heuristicConfig: Assignment.WaterfallByExcessMargin;
  setHeuristicConfig: React.Dispatch<
    React.SetStateAction<Assignment.DecisionHeuristicConfig | undefined>
  >;
  readOnly: boolean;
}) => {
  const {
    filteredTargets: availableTargets,
    allTargetsMap: allTargets,
  } = useMemo(() => {
    // targets that have been already configured
    const configuredTargets = new Set(
      heuristicConfig.config.waterfall.map((config) =>
        config.type === 'balanceSheet'
          ? 'balanceSheet'
          : config.creditFacilityId,
      ),
    );

    const targets: Target[] = creditFacilitiesForAssignmentGroupingData.creditFacilitiesForAssignmentGrouping.map(
      (cf) => ({
        name: cf.name,
        creditFacilityId: cf.id,
        type: 'creditFacility' as const,
      }),
    );
    targets.push({ name: 'Balance Sheet', type: 'balanceSheet' as const });

    // targets that still can be configured
    const filteredTargets = targets.filter((target) =>
      target.type === 'balanceSheet'
        ? !configuredTargets.has('balanceSheet')
        : !configuredTargets.has(target.creditFacilityId),
    );

    const allTargetsMap = targets.reduce<Record<string, Target>>(
      (acc, target) => {
        switch (target.type) {
          case 'balanceSheet':
            // eslint-disable-next-line no-param-reassign
            acc.balanceSheet = target;
            break;
          case 'creditFacility':
            // eslint-disable-next-line no-param-reassign
            acc[target.creditFacilityId] = target;
            break;
          default:
            return finleyAssertUnreachable(target);
        }
        return acc;
      },
      {},
    );

    return { allTargetsMap, filteredTargets };
  }, [creditFacilitiesForAssignmentGroupingData, heuristicConfig]);

  const [selectedTarget, setSelectedTarget] = useState<Target | undefined>(
    availableTargets[0],
  );

  useEffect(() => {
    setSelectedTarget(availableTargets[0]);
  }, [availableTargets]);

  const handleTargetChange = (target: string | undefined) => {
    if (target) {
      const foundTargets = availableTargets.filter(
        (tar) => tar.name === target,
      );
      finleyAssert(
        foundTargets.length === 1,
        'Only one target should exist in Waterfall By Excess Margin.',
      );
      const foundTarget = foundTargets[0];

      setSelectedTarget(foundTarget);
    }
  };

  const addTarget = () => {
    if (!selectedTarget) {
      return;
    }

    setHeuristicConfig((oldConfig) => {
      if (!oldConfig) return oldConfig;

      finleyAssert(
        oldConfig.heuristicType === 'waterfallByExcessMargin',
        'heuristicType must be waterfallByExcessMargin',
      );

      switch (selectedTarget.type) {
        case 'balanceSheet':
          return {
            heuristicType: oldConfig.heuristicType,
            config: {
              waterfall: [
                ...oldConfig.config.waterfall,
                {
                  limitType: 'max',
                  type: 'balanceSheet',
                },
              ],
            },
          };
        case 'creditFacility':
          return {
            heuristicType: oldConfig.heuristicType,
            config: {
              waterfall: [
                ...oldConfig.config.waterfall,
                {
                  creditFacilityId: selectedTarget.creditFacilityId,
                  limitType: 'max',
                  type: 'creditFacility',
                },
              ],
            },
          };
        default:
          return finleyAssertUnreachable(selectedTarget);
      }
    });
  };

  const handleLimitChange = (limitType: 'min' | 'max', idx: number) => {
    setHeuristicConfig((oldConfig) => {
      if (!oldConfig) return oldConfig;

      finleyAssert(
        oldConfig.heuristicType === 'waterfallByExcessMargin',
        'heuristicType must be waterfallByExcessMargin',
      );

      const configs = [...oldConfig.config.waterfall];

      configs[idx].limitType = limitType;

      return {
        heuristicType: oldConfig.heuristicType,
        config: { waterfall: configs },
      };
    });
  };

  const handlePercentageChange = (
    percentage: number | undefined,
    idx: number,
  ) => {
    setHeuristicConfig((oldConfig) => {
      if (!oldConfig) return oldConfig;

      finleyAssert(
        oldConfig.heuristicType === 'waterfallByExcessMargin',
        'heuristicType must be waterfallByExcessMargin',
      );

      const configs = [...oldConfig.config.waterfall];

      if (typeof percentage === 'undefined') {
        configs[idx].percentageRate = undefined;
      } else {
        configs[idx].percentageRate = percentage;
      }

      return {
        heuristicType: oldConfig.heuristicType,
        config: { waterfall: configs },
      };
    });
  };

  const handleValueChange = (value: number | undefined, idx: number) => {
    setHeuristicConfig((oldConfig) => {
      if (!oldConfig) return oldConfig;

      finleyAssert(
        oldConfig.heuristicType === 'waterfallByExcessMargin',
        'heuristicType must be waterfallByExcessMargin',
      );

      const configs = [...oldConfig.config.waterfall];

      if (typeof value === 'undefined') {
        configs[idx].value = undefined;
      } else {
        configs[idx].value = value;
      }

      return {
        heuristicType: oldConfig.heuristicType,
        config: { waterfall: configs },
      };
    });
  };

  const deleteTarget = (idx: number) => {
    setHeuristicConfig((oldConfig) => {
      if (!oldConfig) return oldConfig;

      finleyAssert(
        oldConfig.heuristicType === 'waterfallByExcessMargin',
        'heuristicType must be waterfallByExcessMargin',
      );

      const configs = [...oldConfig.config.waterfall];

      configs.splice(idx, 1);

      return { ...oldConfig, config: { waterfall: configs } };
    });
  };

  return (
    <>
      <Flex mt="24px" mb="24px">
        <Flex direction="column" mr="24px" maxWidth="300px" minWidth="300px">
          <Text fontSize="md" mb="16px" fontWeight="600">
            Decision Heuristic by Waterfall
          </Text>
          <Text fontSize="md" mb="16px" color="gray">
            Assigns receivables in waterfall order according to the specified
            targets. Target percentages are specified as a percentage of the
            assignable receivable expsosure.
          </Text>
        </Flex>
        <Flex
          direction="column"
          width="100%"
          border="1px"
          borderColor={colors.gray[200]}
          borderRadius="8px"
          padding="24px"
          background="#f9f6f1b3"
        >
          {!readOnly && (
            <Flex mb="32px" alignItems="center">
              <Text>Add target</Text>
              <Select
                value={selectedTarget?.name}
                options={availableTargets.map((target) => ({
                  label: target.name,
                  value: target.name,
                }))}
                onChange={handleTargetChange}
                isDisabled={availableTargets.length === 0}
                buttonProps={{ ml: '20px', width: '400px' }}
                containerFlexProps={{}}
              />
              <AddIconButton
                isDisabled={availableTargets.length === 0}
                onClick={addTarget}
              />
            </Flex>
          )}
          {heuristicConfig.config.waterfall.map((config, idx) => {
            return (
              <WaterfallConfiguration
                // eslint-disable-next-line react/no-array-index-key
                key={idx}
                config={config}
                idx={idx}
                readOnly={readOnly}
                deleteTarget={deleteTarget}
                handlePercentageChange={handlePercentageChange}
                handleLimitChange={handleLimitChange}
                handleValueChange={handleValueChange}
                allTargets={allTargets}
              />
            );
          })}
        </Flex>
      </Flex>
      <hr />
    </>
  );
};
